import { useMemo } from 'react'
import {
  Valuation,
  FinalValuation,
  EstimateLogValuation,
  MultiProviderValuation,
  ValuationSource,
} from '@popety-io/types'
import {
  parseLogValuationData,
  parseMultiProviderValuation,
  parseManualValuations,
} from '../utils/valuationParsers'
import { calculateFinalValuation } from '../utils/finalValuationCalculator'

interface UseManualValuationDataReturn {
  valuations: Valuation[]
  finalValuation: FinalValuation
}

// Helper function to detect MultiProviderValuation format
const isMultiProviderValuation = (
  data: EstimateLogValuation | MultiProviderValuation
): data is MultiProviderValuation => {
  // MultiProviderValuation has providers as top-level keys with price, valueRange, confidence properties
  const keys = Object.keys(data)
  return keys.some(
    (key) =>
      (data as Record<string, any>)[key] &&
      typeof (data as Record<string, any>)[key] === 'object' &&
      'price' in (data as Record<string, any>)[key] &&
      'valueRange' in (data as Record<string, any>)[key] &&
      'confidence' in (data as Record<string, any>)[key]
  )
}

/**
 * Hook to parse and filter manual valuations only
 * Accepts priceData (estimate.price) which contains manual valuations in price.manual
 * Also accepts logValuation for backward compatibility
 */
export const useManualValuationData = (
  priceData: any | null,
  logValuation?: EstimateLogValuation | MultiProviderValuation | null
): UseManualValuationDataReturn => {
  // Parse manual valuations from price.manual (new architecture)
  const manualValuationsFromPrice = useMemo(() => {
    if (!priceData) {
      return []
    }
    return parseManualValuations(priceData)
  }, [priceData])

  // Parse all valuations from logValuation (old architecture - for backward compatibility)
  const allValuations = useMemo(() => {
    if (!logValuation) {
      return []
    }

    // Check if it's MultiProviderValuation format (from AVM API)
    if (isMultiProviderValuation(logValuation)) {
      return parseMultiProviderValuation(logValuation)
    }

    // Otherwise, treat as EstimateLogValuation format (raw logs)
    return parseLogValuationData(logValuation as EstimateLogValuation)
  }, [logValuation])

  // Filter to only manual valuations (MANUAL source) from logValuation
  const manualValuationsFromLog = useMemo(() => {
    return allValuations.filter(
      (valuation) => valuation.source === ValuationSource.MANUAL
    )
  }, [allValuations])

  // Combine manual valuations: prioritize priceData, fallback to logValuation for backward compatibility
  const manualValuations = useMemo(() => {
    // If we have manual valuations from priceData (new architecture), use them
    if (manualValuationsFromPrice.length > 0) {
      return manualValuationsFromPrice
    }
    // Otherwise, fallback to manual valuations from logValuation (old architecture)
    return manualValuationsFromLog
  }, [manualValuationsFromPrice, manualValuationsFromLog])

  // Calculate final valuation from manual valuations only
  const finalValuation = useMemo(() => {
    // Pass null for logValuation since we only want to calculate from manual valuations
    // The calculateFinalValuation function will use the valuations array to compute the final value
    return calculateFinalValuation(null, manualValuations)
  }, [manualValuations])

  return {
    valuations: manualValuations,
    finalValuation,
  }
}
